/**
 * Centralized environment configuration.
 * Never use process.env directly in components — import from here.
 *
 * Token keys come from the portal env file loaded by webpack:
 * - .env.admin         → TOKEN_KEY=ds_admin_token
 * - .env.merchant       → TOKEN_KEY=ds_merchant_token
 * - .env.businessuser   → TOKEN_KEY=ds_businessuser_token
 */
const APP_TYPE = process.env.APP_TYPE || 'admin';

/** Build prefixes with `+ '/'` so webpack inlining of `DOMAIN=https://...` cannot start a comment. */
const HTTPS_PREFIX = 'https:' + '/' + '/';
const HTTP_PREFIX = 'http:' + '/' + '/';

const toChottuDomainHost = (value = '') => {
  let host = String(value || '').trim();
  const lower = host.toLowerCase();
  if (lower.startsWith(HTTPS_PREFIX)) host = host.slice(HTTPS_PREFIX.length);
  else if (lower.startsWith(HTTP_PREFIX)) host = host.slice(HTTP_PREFIX.length);
  if (host.endsWith('/')) host = host.slice(0, -1);
  return host;
};

const toChottuBaseUrl = (value = '') => {
  let url = String(value || '').trim();
  if (url.endsWith('/')) url = url.slice(0, -1);
  return url || HTTPS_PREFIX + 'api2.chottulink.com';
};

const PORTAL_TOKEN_KEYS = {
  admin: 'ds_admin_token',
  merchant: 'ds_merchant_token',
  businessuser: 'ds_businessuser_token',
};

const PORTAL_REFRESH_TOKEN_KEYS = {
  admin: 'ds_admin_refresh_token',
  merchant: 'ds_merchant_refresh_token',
  businessuser: 'ds_businessuser_refresh_token',
};

const env = {
  APP_NAME: process.env.APP_NAME || 'Dine Savvy',
  APP_TYPE,
  APP_ENV: process.env.APP_ENV || 'development',
  APP_PORT: process.env.APP_PORT || '3000',
  API_BASE_URL: process.env.API_BASE_URL || 'http://localhost:8000/api/v1',
  IMAGE_BASE_URL:
    process.env.IMAGE_BASE_URL ||
    (process.env.API_BASE_URL
      ? `${String(process.env.API_BASE_URL).replace(/\/$/, '')}/uploads`
      : 'http://localhost:8000/uploads'),
  SOCKET_URL: process.env.SOCKET_URL || 'ws://localhost:8000',
  // From .env.admin / .env.merchant / .env.businessuser (fallbacks match those files).
  TOKEN_KEY: process.env.TOKEN_KEY || PORTAL_TOKEN_KEYS[APP_TYPE] || PORTAL_TOKEN_KEYS.admin,
  REFRESH_TOKEN_KEY:
    process.env.REFRESH_TOKEN_KEY ||
    PORTAL_REFRESH_TOKEN_KEYS[APP_TYPE] ||
    PORTAL_REFRESH_TOKEN_KEYS.admin,
  ENCRYPTION_KEY: process.env.ENCRYPTION_KEY || 'default_encryption_key_32chars!',
  GOOGLE_MAP_KEY: process.env.GOOGLE_MAP_KEY || '',
  GOOGLE_MAP_KEY_SEARCH: process.env.GOOGLE_MAP_KEY_SEARCH || '',
  APP_STORE_URL: process.env.APP_STORE_URL || 'https://apps.apple.com/',
  TESTFLIGHT_URL: process.env.TESTFLIGHT_URL || 'https://testflight.apple.com/',
  PLAY_STORE_URL: process.env.PLAY_STORE_URL || 'https://play.google.com/store',
  VERSION: process.env.VERSION || '1.0.0',
  IS_DEV: process.env.APP_ENV === 'development',
  IS_PROD: process.env.APP_ENV === 'production',
  MOBILE_APP_URL: process.env.MOBILE_APP_URL || '',
  ENCRYPTION_ENABLE: ['true', '1', 'yes'].includes(
    String(process.env.ENCRYPTION_ENABLE ?? 'true')
      .trim()
      .toLowerCase(),
  ),
  /** ChottuLink REST API origin (`CHOTTU_BASE_URL` in `.env.{portal}`), no trailing slash. */
  CHOTTU_BASE_URL: toChottuBaseUrl(process.env.CHOTTU_BASE_URL),
  /** ChottuLink REST API key (`CHOTTU_API_KEY` in `.env.{portal}`). */
  CHOTTU_API_KEY: process.env.CHOTTU_API_KEY || '',
  /**
   * ChottuLink branded domain host only (no protocol), e.g. `dine-savvy.chottu.link`.
   * Reads `CHOTTU_DOMAIN` then `DOMAIN` from `.env.{portal}`.
   * Protocol is stripped without a `//` regex so webpack inlining of
   * `DOMAIN=https://...` cannot comment-out the rest of this assignment.
   */
  CHOTTU_DOMAIN: toChottuDomainHost(process.env.CHOTTU_DOMAIN || process.env.DOMAIN || ''),
};

export default env;
